<template>
  <v-card
    class="pa-5 ml-10 mr-10 mt-5"
    variant="outlined"
    data-cy="promo-box">
    <v-card-title>{{ $t("views.licence.promo.title") }}</v-card-title>
    <v-card-text>
      <!-- active price discount of the licence -->
      <v-chip
        v-if="activeDiscount"
        class="mb-2"
        color="success"
        data-cy="promo-active-discount">
        {{
          $t("views.licence.promo.active", {
            description: promo.describe(activeDiscount),
            code: activeDiscount.code,
            interpolation: { escapeValue: false }
          })
        }}
      </v-chip>

      <v-row
        align="center"
        dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="promo.promoCode.value"
            data-cy="promo-input"
            hide-details="auto"
            density="compact"
            :label="$t('views.licence.promo.label')"
            :disabled="!!promo.appliedCode.value"
            :error-messages="promo.promoError.value ?? undefined"
            @keyup.enter="promo.applyPromo()" />
        </v-col>
        <v-col cols="auto">
          <v-btn
            v-if="!promo.appliedCode.value"
            data-cy="promo-apply"
            :loading="promo.applying.value"
            :disabled="!promo.promoCode.value.trim()"
            @click="promo.applyPromo()">
            {{ $t("views.licence.promo.apply") }}
          </v-btn>
          <v-btn
            v-else
            data-cy="promo-remove"
            @click="promo.removePromo()">
            {{ $t("views.licence.promo.remove") }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- status of the entered code -->
      <v-chip
        v-if="promo.promoInfo.value && promo.appliedCode.value"
        class="mt-2"
        color="success"
        closable
        data-cy="promo-chip"
        @click:close="promo.removePromo()">
        {{ promo.describe(promo.promoInfo.value) }}
      </v-chip>
      <v-alert
        v-else-if="redeemedInfo"
        class="mt-2"
        type="success"
        density="compact"
        data-cy="promo-redeemed">
        {{ $t("views.licence.promo.redeemed", { description: promo.describe(redeemedInfo), interpolation: { escapeValue: false } }) }}
      </v-alert>

      <!-- module trials of the licence -->
      <div
        v-for="trial in trials"
        :key="trial.module"
        class="d-flex align-center mt-3"
        :data-cy="'trial-' + trial.module">
        <span class="mr-2">{{ promo.moduleName(trial.module) }}</span>
        <v-chip
          size="small"
          color="info"
          class="mr-2">
          {{
            $t("views.licence.promo.trialUntil", {
              date: new Date(trial.end_date).toLocaleDateString(),
              count: trial.quantity,
              interpolation: { escapeValue: false }
            })
          }}
        </v-chip>
        <v-btn
          v-if="showBook && (booked?.[trial.module] ?? 0) < trial.quantity"
          size="small"
          data-cy="trial-book"
          @click="emit('book', trial.module, trial.quantity)">
          {{ $t("views.licence.promo.bookPermanently") }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { usePromo } from "./moduleComposable";

const props = defineProps<{
  promo: ReturnType<typeof usePromo>;
  trials?: { module: string; quantity: number; end_date: string }[];
  activeDiscount?: any;
  // book button only where the parent handles @book
  showBook?: boolean;
  // trial slots already booked per module
  booked?: Record<string, number>;
}>();
const emit = defineEmits<{
  (e: "book", module: string, quantity: number): void;
}>();

// a redeemed module trial has no applied code, only the info
const redeemedInfo = computed(() =>
  props.promo.promoInfo.value?.type === "module_trial" &&
  !props.promo.appliedCode.value
    ? props.promo.promoInfo.value
    : null
);
</script>
